import {DAYS_BY_INDEX, Store} from "../models/";
import {html} from "lit";
import {i18nService} from "../../../services/i18nService";
import favouriteStoreIcon from "../../../assets/images/png/favouriteStore.png";
import {MarkerController} from "./marker";
import {storeService} from "../../../services/store-service";
import {createMarkerContent} from "../utils/marker-content";

export class StoreController {

  private markerController: MarkerController;

  constructor(markerController: MarkerController) {
    this.markerController = markerController;
  }

  // =========================
  // 🔹 STORE UTILITY
  // =========================

  getStoreById(id: string | undefined, stores: Store[]) {
    return stores.find(it => it.id === id);
  }

  setMyStore(myStoreId: string, stores: Store[]) {
    const store = this.getStoreById(myStoreId, stores);
    if (!store) {
      return;
    }
    store.isFavourite = true;
    store.marker.content = createMarkerContent(favouriteStoreIcon, 50);
    return store;
  }

  // =========================
  // 🔹 DISTANCE & GEOSPATIAL DATA
  // =========================

  updateStoreDistance(stores: Store[], pos: google.maps.LatLng) {
    return stores
      .map(store => {
        const markerPosition = store.lat && store.lng
          ? new google.maps.LatLng(store.lat, store.lng)
          : null;

        if (!markerPosition) {
          return store;
        }

        const distance = google.maps.geometry.spherical.computeDistanceBetween(pos, markerPosition);
        const km = distance / 1000;

        store.numberDistance = distance;
        store.stringDistance = km.toLocaleString("de-DE", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 1,
        }) + " km";

        return store;
      })
      .sort((a, b) => a.numberDistance - b.numberDistance);
  }

  getVisibleStoresRecursively(map: google.maps.Map | undefined, stores: Store[]): Store[] {
    if (!map) {
      return [];
    }

    const currentBounds = map.getBounds();
    if (!currentBounds) {
      return [];
    }

    const visibleStores = stores.filter(store =>
      currentBounds.contains(new google.maps.LatLng(store.lat, store.lng))
    );

    if (visibleStores.length === 0 && map.getZoom()! > 4) {
      map.setZoom(map.getZoom()! - 1);
      return this.getVisibleStoresRecursively(map, stores);
    }

    return visibleStores;
  }

  createBoundsForClosestStore(store: Store, pos: google.maps.LatLng) {
    const bounds = new google.maps.LatLngBounds();
    bounds.extend(pos);

    if (store.lat && store.lng) {
      bounds.extend(new google.maps.LatLng(store.lat, store.lng));
    }

    return bounds;
  }

  getClosestStores(slice: number, stores: Store[]) {
    return stores.slice(0, slice);
  }

  // =========================
  // 🔹 OPENING HOURS STATUS
  // =========================

  checkStoreOpenings(store: Store) {
    const now = new Date();
    const dayIndex = (now.getDay() + 6) % 7;
    const todayOpening = store?.openings?.[dayIndex];

    if (!todayOpening) {
      return;
    }

    const toMinutes = (timeStr: string) => {
      if (!timeStr) return 0;
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours * 60 + minutes;
    };

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const openingTime = toMinutes(todayOpening?.opening);
    const closingTime = toMinutes(todayOpening?.closing);

    let statusKey = "store.closed";
    let color = "closed";

    if (currentTime >= openingTime && currentTime < closingTime) {
      const closesInOneHour = closingTime - currentTime <= 60;
      statusKey = closesInOneHour ? "store.closing" : "store.open";
      color = closesInOneHour ? "closing" : "open";
    } else if (currentTime < openingTime && openingTime - currentTime <= 60) {
      statusKey = "store.opening";
      color = "opening";
    }

    const status = i18nService?.translate(statusKey);
    return html`<p style="font-weight: 400" class="status ${color}">${status}</p>`;
  }

  getTodayClosingTime(store: Store): string {
    const currentDay = DAYS_BY_INDEX[new Date().getDay()]?.toLowerCase();
    const today = store?.openings?.find(it => it.week_day?.toLowerCase() === currentDay);

    if (!today?.closing) {
      return '';
    }

    const now = new Date();
    const currentTime = now.toTimeString().slice(0, 5);

    if (currentTime < today.closing) {
      return `${i18nService?.translate('store.closes.at')} ${today.closing}`;
    }

    return '';
  }

  // =========================
  // 🔹 FAVORITE STORE SYNC
  // =========================

  subscribeToFavouriteStore(stores: Store[], favouriteId: string | undefined, setFavouriteId: (id: string) => void, setCurrentStore: (store: Store | undefined) => void) {
    storeService.subscribeForMyStoreId((profileStoreId: string) => {
      const store = this.setMyStore(profileStoreId, stores);

      if (store) {
        setCurrentStore(store);
        setFavouriteId(store.id);
      } else {
        this.markerController.resetStore(stores, favouriteId);
        setFavouriteId("");
      }
    });
  }

  setFavouriteStore(store: Store | undefined, isLoggedIn: () => boolean, favouriteId: string | undefined, stores: Store[], setFavouriteId: (id: string) => void, showLoginPrompt: () => void) {
    if (!isLoggedIn()) {
      showLoginPrompt();
      return;
    }

    if (store?.id === favouriteId) {
      this.markerController.resetStore(stores, favouriteId);
      storeService.saveMyStore("");
      setFavouriteId("");
    } else if (store) {
      storeService.storeCallback(store, store.id);
      storeService.saveMyStore(store.id);
      this.markerController.resetStore(stores, favouriteId);
      setFavouriteId(store.id);
    }
  }

  // =========================
  // 🔹 STORE SEARCH FILTERING
  // =========================

  filterStoresBySearchTerm(stores: Store[], searchInput: string, favouriteId?: string): Store[] {
    if (!searchInput) {
      const favStore = favouriteId ? stores.find(s => s.id === favouriteId) : null;
      return favStore ? [favStore] : [];
    }

    const searchTermsArray = searchInput.trim().split(/\s+/);
    let filteredStores = stores.filter(store =>
      searchTermsArray.every(term =>
        `${store.address_line_1} ${store.address_line_2} ${store.address_line_3}`
          .toLowerCase()
          .includes(term.toLowerCase())
      )
    );

    if (favouriteId && filteredStores.some(s => s.id === favouriteId)) {
      filteredStores = filteredStores.filter(s => s.id !== favouriteId);
      const favStore = stores.find(s => s.id === favouriteId)!;
      filteredStores.unshift(favStore);
    }

    return filteredStores;
  }
}